import React, { useEffect, useState } from "react";
import { GridPaginationModel, GridRowId, GridSortModel } from "@mui/x-data-grid-pro";
import { useAsyncState } from "src/utils/async/asyncState";
import { AnyError } from "src/errorHandling/anyError";
import { logError } from "src/errorHandling/errorLogging";
import { DEFAULT_PAGINATION_MODEL } from "src/components/common/dataGrid/utils";
import { ControlledAsyncCrudDataGridProps } from "src/components/common/dataGrid/crud/ControlledAsyncCrudDataGrid.tsx";
import { dataGridPersistentStateProps } from "src/components/common/dataGrid/dataGridStateStorage.ts";
import {
	ServerSideDataModelRequest,
	ServerSideDataModelRequest_SortItem,
} from "src/api/generated/common/dataGrids/serverSideDataModelRequest.ts";
import { ServerSideDataModelResult } from "src/api/generated/common/dataGrids/serverSideDataModelResult";
import { AavoDataGridApi } from "src/components/common/dataGrid/AavoDataGridApi.ts";
import { useForwardedRef } from "src/utils/useForwardedRef.ts";
import { useMaybeControlledState } from "src/utils/useMaybeControlledState.ts";

export interface UseServerSideDataGridReturn<
	RowData extends object,
	TParams extends ServerSideDataModelApiParams<any>,
> {
	selectedRows: RowData[];
	onlySelectedRow: RowData | undefined;
	setSelectedRows: (rows: RowData[]) => void;
	serverSideDataGridProps: ServerSideDataGridProps<RowData>;
	currentParams: TParams;
	// Updates currents params and refreshes data
	refreshData: (params?: Partial<TParams>) => Promise<RowData[]>;
	setLocalRow: (index: number, setter: (currentValue: RowData) => RowData) => void;
}

export type ServerSideDataModelApiParams<T extends (...args: any) => any> = Omit<
	Parameters<T>[0],
	// "dataGridRequest" is deprecated form
	"dataModelRequest" | "dataGridRequest"
>;

export type ParamsWithDataGridRequest<TParams> = TParams & {
	dataModelRequest: ServerSideDataModelRequest;
	dataGridRequest: ServerSideDataModelRequest;
};

export type ServerSideDataGridProps<RowData extends object> = Required<
	Pick<
		ControlledAsyncCrudDataGridProps<RowData>,
		| "rowsAsync"
		| "refreshData"
		| "pagination"
		| "disableColumnFilter"
		| "sortingMode"
		| "paginationMode"
		| "onSortModelChange"
		| "onPaginationModelChange"
		| "rowCount"
		| "hideFooter"
		| "initialState"
		| "onStateChange"
		| "selectedRows"
		| "onRowSelectionChanged"
		| "getRowId"
		| "apiRef"
	>
>;

export interface UseServerSideDataModelParams<
	RowData extends object,
	TParams extends ServerSideDataModelApiParams<any>,
> {
	gridId: string;
	fetchData: (
		params: ParamsWithDataGridRequest<TParams>,
	) => Promise<ServerSideDataModelResult<RowData>>;
	initialParams: TParams;
	getRowId: (row: RowData) => GridRowId;
	afterRefresh?: (newRows: RowData[]) => void;
	apiRef?: React.MutableRefObject<AavoDataGridApi | null>;
	usePagination?: boolean;
	selectedRows?: RowData[];
	onSelectionChanged?: (rows: RowData[]) => void;
}

export const useServerSideDataModel = <
	RowData extends object,
	TParams extends ServerSideDataModelApiParams<any>,
>({
	gridId,
	getRowId,
	fetchData,
	initialParams,
	afterRefresh,
	apiRef: apiRefProp,
	usePagination = true,
	selectedRows: selectedRowsProp,
	onSelectionChanged: setSelectedRowsProp,
}: UseServerSideDataModelParams<RowData, TParams>): UseServerSideDataGridReturn<
	RowData,
	TParams
> => {
	const apiRef = useForwardedRef(apiRefProp);
	const [currentParams, setCurrentParams] = useState(initialParams);
	const [dataAsync, setDataAsync, setDataImmediate] =
		useAsyncState<ServerSideDataModelResult<RowData>>();
	const [error, setError] = useState<AnyError | undefined>(undefined);

	const { initialState: initialGridState, onStateChange: onGridStateChange } =
		dataGridPersistentStateProps(gridId);

	const [sortModel, setSortModel] = useState<GridSortModel>(
		initialGridState.sorting?.sortModel ?? [],
	);

	const [paginationModel, setPaginationModel] = useState<GridPaginationModel | undefined>({
		pageSize:
			initialGridState?.pagination?.paginationModel?.pageSize ??
			DEFAULT_PAGINATION_MODEL.pageSize,
		page: 0,
	});

	const [selectedRows, setSelectedRows] = useMaybeControlledState({
		controlledValue: selectedRowsProp,
		onChange: setSelectedRowsProp,
		defaultValue: [],
	});
	const onlySelectedRow = selectedRows.length === 1 ? selectedRows[0] : undefined;

	const handleSortModelChange = async (model: GridSortModel) => {
		setSortModel(model);
		await refreshDataWithParams(model, paginationModel, currentParams);
	};

	const handlePaginationModelChange = async (model: GridPaginationModel) => {
		setPaginationModel(model);
		await refreshDataWithParams(sortModel, model, currentParams);
	};

	const refreshDataWithParams = async (
		sortModel: GridSortModel,
		paginationModel: GridPaginationModel | undefined,
		params: TParams,
	) => {
		try {
			const dataModelRequest = {
				sortModel: mapSortModelToBackend(sortModel),
				paginationModel: usePagination ? paginationModel : undefined,
			};
			const paramsWithDataGridRequest = {
				...params,
				dataModelRequest: dataModelRequest,
				dataGridRequest: dataModelRequest,
			};
			const newData = await setDataAsync(() => fetchData(paramsWithDataGridRequest));

			const newSelectedRows = newData.rows.filter((row) =>
				selectedRows.some((selectedRow) => getRowId(selectedRow) === getRowId(row)),
			);
			setSelectedRows(newSelectedRows);

			afterRefresh?.(newData.rows);

			return newData.rows;
		} catch (e) {
			setError(e as AnyError);
			return [];
		}
	};

	const refreshData = (params: Partial<TParams> = {}) => {
		setCurrentParams((prev) => {
			return {
				...prev,
				...params,
			};
		});
		return refreshDataWithParams(sortModel, paginationModel, {
			...currentParams,
			...params,
		});
	};

	const setLocalRow = (index: number, setter: (currentValue: RowData) => RowData) => {
		setDataImmediate((currentData) => {
			if (!currentData) {
				return currentData;
			}
			const currentRow = currentData.rows[index];
			if (currentRow === undefined) {
				return currentData;
			}
			const newRows = [...currentData.rows];
			newRows[index] = setter(currentRow);
			return {
				...currentData,
				rows: newRows,
			};
		});
	};

	useEffect(() => {
		refreshData().catch((e) => {
			logError(e as AnyError);
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return {
		serverSideDataGridProps: {
			apiRef: apiRef,
			pagination: true,
			disableColumnFilter: true,
			hideFooter: !usePagination,
			sortingMode: "server",
			paginationMode: "server",
			onSortModelChange: handleSortModelChange,
			onPaginationModelChange: handlePaginationModelChange,
			rowCount: dataAsync.data?.totalRowCount ?? 0,
			initialState: initialGridState,
			onStateChange: onGridStateChange,
			refreshData: refreshData,
			selectedRows: selectedRows,
			onRowSelectionChanged: setSelectedRows,
			getRowId: getRowId,
			rowsAsync: {
				data: dataAsync.data?.rows,
				loading: dataAsync.loading,
				error: error,
			},
		},
		currentParams: currentParams,
		refreshData: refreshData,
		setLocalRow: setLocalRow,
		selectedRows: selectedRows,
		setSelectedRows: setSelectedRows,
		onlySelectedRow: onlySelectedRow,
	};
};

const mapSortModelToBackend = (sortModel: GridSortModel) =>
	sortModel.map((sortItem) => {
		const ret: ServerSideDataModelRequest_SortItem = {
			field: sortItem.field,
			sort: null,
		};
		switch (sortItem.sort) {
			case "asc":
				ret.sort = "ASC";
				break;
			case "desc":
				ret.sort = "DESC";
				break;
			default:
				ret.sort = undefined;
				break;
		}
		return ret;
	});
